import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { extractVerifyScalars } from './verifyExtractor';

/**
 * Hook agnóstico que faz call debounced ao endpoint de verify do checkout.
 *
 * As 2 callsites (MY checkout + CRM Nova Encomenda) injectam apenas a
 * função `verifyFn(payload) => Promise<spResult|null>` adequada ao seu
 * endpoint. O `payload` recebido inclui transport, paymentMethod, shipping
 * address, applied discounts e applied promo — que callsites passam pelo
 * objecto `inputs`.
 *
 * O SP corre desde que tenha `transporte + morada de envio + items no
 * carrinho` (o método de pagamento NÃO é necessário para calcular limites
 * de vales/créditos — só afecta descontos Cofidis). Logo conseguimos mostrar
 * "Consegue utilizar" cap por total mesmo antes do utilizador escolher o
 * pagamento.
 *
 * O resultado vem normalizado via `extractVerifyScalars` em `data` + `raw`.
 */
export default function useVerifyChecker({ verifyFn, inputs, balances, debounceMs = 400 }) {
  const [verifyData, setVerifyData] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const timer = useRef(null);

  const {
    cartLength, selectedTransport, selectedPayment, selectedEnvio,
    appliedVales, appliedCreditos, appliedPromo,
  } = inputs;

  const callVerify = useCallback(async (overridePromo) => {
    if (!selectedTransport || !selectedEnvio || !cartLength) return null;
    setVerifying(true);
    try {
      const spResult = await verifyFn({
        selectedTransport,
        selectedPayment: selectedPayment || '',
        selectedEnvio,
        appliedVales: Number(appliedVales || 0),
        appliedCreditos: Number(appliedCreditos || 0),
        appliedPromo: overridePromo !== undefined ? overridePromo : (appliedPromo || ''),
      });
      setVerifyData(spResult || null);
      return spResult;
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.warn('useVerifyChecker failed', e);
      setVerifyData(null);
      return null;
    } finally {
      setVerifying(false);
    }
  }, [verifyFn, cartLength, selectedTransport, selectedPayment, selectedEnvio,
      appliedVales, appliedCreditos, appliedPromo]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(callVerify, debounceMs);
    return () => clearTimeout(timer.current);
  }, [callVerify, debounceMs]);

  const scalars = useMemo(
    () => extractVerifyScalars(verifyData, balances),
    [verifyData, balances],
  );

  return {
    verifyData, verifying, callVerify,
    scalars,
    // Legacy aliases — mantém retro-compatibilidade com `useCheckoutVerify`
    // do CRM (mesma shape de retorno) para que callsites antigas continuem
    // a funcionar sem mudar a UI.
    spTotal: verifyData,
    spSubtotal: scalars.subtotal,
    spPortes: scalars.portes,
    spIva: scalars.iva,
    spDescontos: scalars.descontos,
    spTotalPagar: scalars.total,
    spValesPermitidos: scalars.valesPermitidos,
    spValesExistentes: scalars.valesExistentes,
    spCreditosPermitidos: scalars.creditosPermitidos,
    spCreditosExistentes: scalars.creditosExistentes,
    spValorDescontoCP: scalars.valorDescontoCP,
    spDescontoVale: scalars.descontoVale,
    spDescontoCreditos: scalars.descontoCreditos,
  };
}
